/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import React, { Fragment, FunctionComponent } from 'react';
import styled from 'styled-components';

import { Product } from '@toucan/client/global/product';

import { styles } from './product-card-price.styles';

export type PriceProps = {
    /**
     * If a loading state should be displayed
     */
    isLoading?: boolean;

    /**
     * The product we are displaying
     */
    product?: Product;

    className?: string;
};

const StyledPrice = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Price: FunctionComponent<PriceProps> = ({
    isLoading,
    product,
    ...otherProps
}: PriceProps) => {
    if (!isLoading && !product) {
        return null;
    }

    const { price, discountPrice } = product || {};

    const renderSkeleton = () => {
        return (
            <Skeleton
                variant={SkeletonVariant.TEXT}
                data-testid="product-card__price__skeleton"
            />
        );
    };

    const renderPrice = () => {
        return (
            <Fragment>
                {price && (
                    <Heading
                        className="price"
                        data-testid="product-card__price__price"
                        size={HeadingSize.H4}
                    >
                        {discountPrice ?? price}
                    </Heading>
                )}
                {discountPrice && (
                    <span
                        className="price-before-discount"
                        data-testid="product-card__price__price-before-discount"
                    >
                        {price}
                    </span>
                )}
            </Fragment>
        );
    };

    return (
        <StyledPrice {...otherProps}>
            {isLoading ? renderSkeleton() : renderPrice()}
        </StyledPrice>
    );
};
